import React, { useEffect, useState } from "react";
import BootstrapModal from "react-bootstrap/Modal";
import Select from "react-select";
import { reactSelectSMStyle, styles } from "../../../../Utils/Common";
import useLang from "./../../../../Shared/hooks/useLanguage";

export interface FilterOption {
  value: number;
  label: string;
}

export interface ScheduleConfig {
  name: string;
  filterType: FilterOption | null;
  frequency: "" | "Daily" | "Weekly" | "Monthly";
  daysOfWeek: string[];
  dayOfWeek: string;
  dayOfMonth: number | null;
  hour: number;
  minute: number;
  meridiem: "AM" | "PM";
  beginDate: string;
  endDate: string;
  dataRange: string;
}

interface ScheduleReportModalProps {
  show: boolean;
  saving: boolean;
  filterOptions: FilterOption[];
  initialFilterLabel?: string;
  onHide: () => void;
  onSave: (config: ScheduleConfig) => void;
}

const WEEK_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const DATA_RANGE_OPTIONS: Record<string, string[]> = {
  Daily: [
    "Yesterday",
    "Yesterday to Date",
    "Last 7 Days to Date",
    "Week to Date",
    "Last Week",
    "Month to Date",
    "Year to Date",
  ],
  Weekly: [
    "Last 7 Days to Date",
    "Week to Date",
    "Last Week",
    "Month to Date",
    "Year to Date",
  ],
  Monthly: ["Last Month", "Last 30 Days", "Year to Date"],
};

const emptyConfig: ScheduleConfig = {
  name: "",
  filterType: null,
  frequency: "",
  daysOfWeek: [...WEEK_DAYS],
  dayOfWeek: "",
  dayOfMonth: null,
  hour: 12,
  minute: 0,
  meridiem: "AM",
  beginDate: "",
  endDate: "",
  dataRange: "",
};

const ScheduleReportModal = ({
  show,
  saving,
  filterOptions,
  initialFilterLabel,
  onHide,
  onSave,
}: ScheduleReportModalProps) => {
  const { t } = useLang();

  const [config, setConfig] = useState<ScheduleConfig>(emptyConfig);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (show) {
      const preselectedFilter =
        filterOptions.find((option) => option.label === initialFilterLabel) ||
        null;
      setConfig({ ...emptyConfig, filterType: preselectedFilter });
      setErrors({});
    }
  }, [show]);

  const clearError = (...fields: string[]) => {
    setErrors((prev) => {
      const next = { ...prev };
      fields.forEach((field) => delete next[field]);
      return next;
    });
  };

  const frequencyOptions = [
    { value: "Daily", label: t("Daily") },
    { value: "Weekly", label: t("Weekly") },
    { value: "Monthly", label: t("Monthly") },
  ];

  const handleFrequencyChange = (option: any) => {
    const frequency = option.value as ScheduleConfig["frequency"];
    setConfig((prev) => ({
      ...prev,
      frequency,
      daysOfWeek: frequency === "Daily" ? [...WEEK_DAYS] : [],
      dayOfWeek: "",
      dayOfMonth: null,
      dataRange: "",
    }));
    clearError("frequency", "days", "dataRange");
  };

  const toggleDailyDay = (day: string) => {
    setConfig((prev) => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter((d) => d !== day)
        : [...prev.daysOfWeek, day],
    }));
    clearError("days");
  };

  const validate = () => {
    const validationErrors: Record<string, string> = {};

    if (!config.name.trim()) {
      validationErrors.name = t("Name is required.");
    }
    if (!config.filterType) {
      validationErrors.filterType = t("Filter is required.");
    }
    if (!config.frequency) {
      validationErrors.frequency = t("Frequency is required.");
    }
    if (config.frequency === "Daily" && config.daysOfWeek.length === 0) {
      validationErrors.days = t("Select at least one day.");
    }
    if (config.frequency === "Weekly" && !config.dayOfWeek) {
      validationErrors.days = t("Select a day of the week.");
    }
    if (config.frequency === "Monthly" && !config.dayOfMonth) {
      validationErrors.days = t("Select a date of the month.");
    }
    if (!config.beginDate) {
      validationErrors.beginDate = t("Begin Date is required.");
    }
    if (!config.endDate) {
      validationErrors.endDate = t("End Date is required.");
    } else if (config.beginDate && config.endDate < config.beginDate) {
      validationErrors.endDate = t("End Date must be on or after Begin Date.");
    }
    if (config.frequency && !config.dataRange) {
      validationErrors.dataRange = t("Select a data range.");
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSaveClick = () => {
    if (!validate()) return;
    onSave(config);
  };

  const renderDaySelection = () => {
    if (config.frequency === "Daily") {
      return (
        <div className="mt-3">
          <label className="required">{t("Days")}</label>
          <div className="d-flex flex-wrap gap-3 mt-1">
            {WEEK_DAYS.map((day) => (
              <label
                key={day}
                className="form-check form-check-sm form-check-solid"
                htmlFor={`ScheduleDay${day}`}
              >
                <input
                  id={`ScheduleDay${day}`}
                  className="form-check-input h-20px w-20px"
                  type="checkbox"
                  checked={config.daysOfWeek.includes(day)}
                  onChange={() => toggleDailyDay(day)}
                />
                {t(day)}
              </label>
            ))}
          </div>
          {errors.days && (
            <div className="text-danger fs-8 mt-1">{errors.days}</div>
          )}
        </div>
      );
    }

    if (config.frequency === "Weekly") {
      return (
        <div className="mt-3">
          <label className="required">{t("Day of Week")}</label>
          <div className="d-flex flex-wrap gap-3 mt-1">
            {WEEK_DAYS.map((day) => (
              <label
                key={day}
                className="form-check form-check-sm form-check-solid"
                htmlFor={`ScheduleWeeklyDay${day}`}
              >
                <input
                  id={`ScheduleWeeklyDay${day}`}
                  className="form-check-input h-20px w-20px rounded-4"
                  type="radio"
                  name="scheduleWeeklyDay"
                  checked={config.dayOfWeek === day}
                  onChange={() => {
                    setConfig((prev) => ({ ...prev, dayOfWeek: day }));
                    clearError("days");
                  }}
                />
                {t(day)}
              </label>
            ))}
          </div>
          {errors.days && (
            <div className="text-danger fs-8 mt-1">{errors.days}</div>
          )}
        </div>
      );
    }

    if (config.frequency === "Monthly") {
      return (
        <div className="mt-3">
          <label className="required">{t("Date of Month")}</label>
          <div
            className="mt-1"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: "4px",
              maxWidth: "320px",
            }}
          >
            {Array.from({ length: 28 }, (_, i) => i + 1).map((date) => (
              <button
                key={date}
                id={`ScheduleMonthDay${date}`}
                type="button"
                className={`btn btn-sm p-1 ${
                  config.dayOfMonth === date ? "btn-primary" : "btn-light"
                }`}
                onClick={() => {
                  setConfig((prev) => ({ ...prev, dayOfMonth: date }));
                  clearError("days");
                }}
              >
                {date}
              </button>
            ))}
          </div>
          {errors.days && (
            <div className="text-danger fs-8 mt-1">{errors.days}</div>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <BootstrapModal show={show} onHide={onHide} backdrop="static" size="lg">
      <BootstrapModal.Header closeButton className="bg-light-primary m-0 p-5">
        <h4>{t("Please select a Frequency and Time")}</h4>
      </BootstrapModal.Header>
      <BootstrapModal.Body className="py-3">
        <div className="row">
          <div className="col-md-6">
            <label className="required">{t("Name")}</label>
            <input
              id="RequisitionReportName"
              type="text"
              name="fileName"
              className="form-control bg-white w-100 rounded-2 fs-8 h-30px"
              placeholder={t("Name")}
              value={config.name}
              onChange={(e) => {
                setConfig((prev) => ({ ...prev, name: e.target.value }));
                clearError("name");
              }}
            />
            {errors.name && (
              <div className="text-danger fs-8 mt-1">{errors.name}</div>
            )}
          </div>
          <div className="col-md-6">
            <label className="required">{t("Filter")}</label>
            <Select
              inputId="ScheduleReportFilter"
              menuPortalTarget={document.body}
              theme={(theme: any) => styles(theme)}
              styles={reactSelectSMStyle}
              options={filterOptions}
              placeholder={t("Select...")}
              value={config.filterType}
              onChange={(option: any) => {
                setConfig((prev) => ({ ...prev, filterType: option }));
                clearError("filterType");
              }}
            />
            {errors.filterType && (
              <div className="text-danger fs-8 mt-1">{errors.filterType}</div>
            )}
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-md-6">
            <label className="required">{t("Frequency")}</label>
            <Select
              inputId="RequisitionReportFrequency"
              menuPortalTarget={document.body}
              theme={(theme: any) => styles(theme)}
              styles={reactSelectSMStyle}
              options={frequencyOptions}
              placeholder={t("Select...")}
              value={
                frequencyOptions.find(
                  (option) => option.value === config.frequency
                ) || null
              }
              onChange={handleFrequencyChange}
            />
            {errors.frequency && (
              <div className="text-danger fs-8 mt-1">{errors.frequency}</div>
            )}
          </div>
          <div className="col-md-6">
            <label className="required">{t("Time")}</label>
            <div className="d-flex align-items-center gap-2">
              <select
                id="ScheduleTimeHour"
                className="form-select form-select-sm w-75px"
                value={config.hour}
                onChange={(e) =>
                  setConfig((prev) => ({
                    ...prev,
                    hour: Number(e.target.value),
                  }))
                }
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((hour) => (
                  <option key={hour} value={hour}>
                    {String(hour).padStart(2, "0")}
                  </option>
                ))}
              </select>
              <span>:</span>
              <select
                id="ScheduleTimeMinute"
                className="form-select form-select-sm w-75px"
                value={config.minute}
                onChange={(e) =>
                  setConfig((prev) => ({
                    ...prev,
                    minute: Number(e.target.value),
                  }))
                }
              >
                {Array.from({ length: 60 }, (_, i) => i).map((minute) => (
                  <option key={minute} value={minute}>
                    {String(minute).padStart(2, "0")}
                  </option>
                ))}
              </select>
              <div className="btn-group">
                {(["AM", "PM"] as const).map((meridiem) => (
                  <button
                    key={meridiem}
                    id={`ScheduleTime${meridiem}`}
                    type="button"
                    className={`btn btn-sm py-1 px-3 ${
                      config.meridiem === meridiem
                        ? "btn-primary"
                        : "btn-light"
                    }`}
                    onClick={() =>
                      setConfig((prev) => ({ ...prev, meridiem }))
                    }
                  >
                    {meridiem}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {renderDaySelection()}

        <div className="row mt-3">
          <div className="col-md-6">
            <label className="required">{t("Begin Date")}</label>
            <input
              id="ScheduleBeginDate"
              type="date"
              name="beginDate"
              className="form-control bg-white rounded-2 fs-8 h-30px"
              value={config.beginDate}
              onChange={(e) => {
                setConfig((prev) => ({ ...prev, beginDate: e.target.value }));
                clearError("beginDate", "endDate");
              }}
            />
            {errors.beginDate && (
              <div className="text-danger fs-8 mt-1">{errors.beginDate}</div>
            )}
          </div>
          <div className="col-md-6">
            <label className="required">{t("End Date")}</label>
            <input
              id="ScheduleEndDate"
              type="date"
              name="endDate"
              className="form-control bg-white rounded-2 fs-8 h-30px"
              min={config.beginDate || undefined}
              value={config.endDate}
              onChange={(e) => {
                setConfig((prev) => ({ ...prev, endDate: e.target.value }));
                clearError("endDate");
              }}
            />
            {errors.endDate && (
              <div className="text-danger fs-8 mt-1">{errors.endDate}</div>
            )}
          </div>
        </div>

        {config.frequency && (
          <div className="mt-3">
            <label className="required">{t("Data Range")}</label>
            <div className="d-flex flex-wrap gap-3 mt-1">
              {DATA_RANGE_OPTIONS[config.frequency].map((option) => (
                <label
                  key={option}
                  className="form-check form-check-sm form-check-solid"
                  htmlFor={`ScheduleDataRange${option.replace(/\s+/g, "")}`}
                >
                  <input
                    id={`ScheduleDataRange${option.replace(/\s+/g, "")}`}
                    className="form-check-input h-20px w-20px rounded-4"
                    type="radio"
                    name="scheduleDataRange"
                    checked={config.dataRange === option}
                    onChange={() => {
                      setConfig((prev) => ({ ...prev, dataRange: option }));
                      clearError("dataRange");
                    }}
                  />
                  {t(option)}
                </label>
              ))}
            </div>
            {errors.dataRange && (
              <div className="text-danger fs-8 mt-1">{errors.dataRange}</div>
            )}
          </div>
        )}
      </BootstrapModal.Body>
      <BootstrapModal.Footer className="p-0">
        <button
          id="RequisitionReportCancel"
          type="button"
          className="btn btn-secondary"
          onClick={onHide}
        >
          {t("Cancel")}
        </button>
        <button
          id="RequisitionReportSave"
          type="button"
          className="btn btn-danger m-2"
          onClick={handleSaveClick}
          disabled={saving}
        >
          {t("Save")}
        </button>
      </BootstrapModal.Footer>
    </BootstrapModal>
  );
};

export default ScheduleReportModal;
